import { RouteObject } from "react-router-dom";
import { Components, roleInitialPaths } from "./routeConfig";

/**
 * Get routes configuration for a specific role
 * @param role - User role string
 * @returns Array of RouteObject for the given role
 */
export function getRoutes(role: string | null): RouteObject[] {
  if (!role) {
    return [];
  }

  switch (role) {
    case "Super_Admin":
      return [
        { path: "dashboard", element: <Components.SuperAdminDashboard /> },
        { path: "manage-leas", element: <Components.ManageLEAPage /> },
        {
          path: "manage-leas/schools",
          element: <Components.ManageSchoolsPage />,
        },
        { path: "assignment-master", element: <Components.AssignmentMaster /> },
        { path: "reports", element: <Components.SuperAdminReport /> },
      ];

    case "LEA_Admin":
      return [
        { path: "dashboard", element: <Components.DistrictAdminDashboard /> },
        { path: "manage-admins", element: <Components.ManageLEAAdmins /> },
        { path: "manage-schools", element: <Components.ManageSchoolsPage /> },
        { path: "assignment-master", element: <Components.AssignmentMaster /> },
        { path: "reports", element: <Components.DistrictAdminReportPage /> },
        {
          path: "recovery-requests",
          element: <Components.AttendaceRecoveryRequests />,
        },
        {
          path: "all-recovery-requests",
          element: <Components.AllRecoveryRequests />,
        },
      ];

    case "School_Admin":
      return [
        { path: "dashboard", element: <Components.SchoolDashboard /> },
        { path: "reports", element: <Components.SchoolReportPage /> },
        { path: "school-users", element: <Components.RoastInfoScreen /> },
        {
          path: "recovery-requests",
          element: <Components.AttendaceRecoveryRequests />,
        },
        {
          path: "all-recovery-requests",
          element: <Components.AllRecoveryRequests />,
        },
        {
          path: "master/others",
          element: <Components.Others />,
        },
        {
          path: "SIS-Import",
          element: <Components.SisImport />,
        },
      ];

    case "Parent":
      return [
        {
          path: "attendance-recovery/list",
          element: <Components.LeavesPage />,
        },
        {
          path: "attendance-recovery/calendar",
          element: <Components.LeavesPage />,
        },
        {
          path: "recovery-requests",
          element: <Components.AttendaceRecoveryRequests />,
        },
      ];

    case "Teacher":
      return [
        {
          path: "recovery-requests",
          element: <Components.AttendaceRecoveryRequests />,
        },
        {
          path: "submitted-assignments",
          element: <Components.AssignmentsPage />,
        },
        {
          path: "all-recovery-requests",
          element: <Components.AllRecoveryRequests />,
        },
      ];

    case "Student":
      return [
        { path: "assignments", element: <Components.AssignmentsPage /> },
        { path: "downloaded-meterials", element: <Components.Materials /> },
      ];

    case "AttandanceCoordinator":
      return [
        {
          path: "attendance-recovery-manual-form",
          element: <Components.AttendaceRecoveryManualForm />,
        },
        {
          path: "recovery-requests",
          element: <Components.AttendaceRecoveryRequests />,
        },
        {
          path: "all-recovery-requests",
          element: <Components.AllRecoveryRequests />,
        },
        {
          path: "submitted-assignments",
          element: <Components.AssignmentsPage />,
        },
        {
          path: "reports",
          element: <Components.ClerkReportPage />,
        },
      ];

    case "Clerk":
      return [
        {
          path: "attendance-recovery-manual-form",
          element: <Components.AttendaceRecoveryManualForm />,
        },
        {
          path: "recovery-requests",
          element: <Components.AttendaceRecoveryRequests />,
        },
        {
          path: "all-recovery-requests",
          element: <Components.AllRecoveryRequests />,
        },
        {
          path: "submitted-assignments",
          element: <Components.AssignmentsPage />,
        },
        {
          path: "reports",
          element: <Components.ClerkReportPage />,
        },
      ];

    default:
      return [];
  }
}

/**
 * Get the initial path for a user role after authentication
 * @param role - User role string
 * @returns Initial path string or null if invalid role
 */
export function getInitialPath(role: string | null): string | null {
  // If role is invalid, return null to handle redirect to login at router level
  if (!role) {
    return null;
  }

  // Handle Default_Role by returning null (unauthenticated users)
  if (role === "Default_Role") {
    return null;
  }

  // Use the configuration object for cleaner code
  return roleInitialPaths[role] || null;
}

/**
 * Check if a role is valid and has defined routes
 * @param role - User role string
 * @returns Boolean indicating if role is valid
 */
export function isValidRole(role: string | null): boolean {
  if (!role || role === "Default_Role") {
    return false;
  }

  return Object.keys(roleInitialPaths).includes(role);
}
